import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GraduationCap,
  Search,
  Plus,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  BookOpen,
  AlertCircle,
  Download,
  MapPin,
  User,
  Star,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import adminService from '../../services/admin.service';
import type { Training } from '../../services/admin.service';
import ApprovalStatusBadge from '../../components/common/ApprovalStatusBadge';

const TrainingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterApproval, setFilterApproval] = useState(searchParams.get('filter') || 'all');
  const [selectedTraining, setSelectedTraining] = useState<Training | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [trainingDetails, setTrainingDetails] = useState<Training | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [notification, setNotification] = useState<{
    title: string;
    subtitle: string;
    kind: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  useEffect(() => {
    loadTrainings();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefreshEnabled) return;
    
    const interval = setInterval(() => {
      loadTrainings();
      setLastRefresh(new Date());
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefreshEnabled]);

  const loadTrainings = async () => {
    try {
      setLoading(true);
      const response = await adminService.getTrainings(1, 1000, '', ''); // Get all trainings
      setTrainings(response.trainings);
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de charger les formations',
        kind: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTraining = async (trainingId: string) => {
    try {
      const training = trainings.find(t => t.id === trainingId);
      if (!training) return;

      await adminService.approveTraining(trainingId);
      
      // Send notification to training center
      try {
        await adminService.sendTrainingNotification(
          training.trainingCenter.id,
          trainingId,
          'approved',
          training.title
        );
      } catch (notificationError) {
        // Don't fail the approval if notification fails
      }

      setTrainings(trainings.map(training =>
        training.id === trainingId ? { ...training, status: 'approved', isApproved: true } : training
      ));
      setNotification({
        title: 'Succès',
        subtitle: 'Formation approuvée avec succès. Notification envoyée au centre de formation.',
        kind: 'success',
      });
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible d\'approuver la formation',
        kind: 'error',
      });
    }
  };

  const handleRejectTraining = async () => {
    if (!selectedTraining || !rejectReason.trim()) return;
    
    try {
      await adminService.rejectTraining(selectedTraining.id, rejectReason);
      
      // Send notification to training center
      try {
        await adminService.sendTrainingNotification(
          selectedTraining.trainingCenter.id,
          selectedTraining.id,
          'rejected',
          selectedTraining.title,
          rejectReason
        );
      } catch (notificationError) {
        // Don't fail the rejection if notification fails
      }

      setTrainings(trainings.map(training => 
        training.id === selectedTraining.id ? { 
          ...training, 
          status: 'refused',
          isApproved: false, 
          rejection_reason: rejectReason,
          rejectionReason: rejectReason 
        } : training
      ));
      setIsRejectModalOpen(false);
      setSelectedTraining(null);
      setRejectReason('');
      setNotification({
        title: 'Succès',
        subtitle: 'Formation rejetée avec succès. Notification envoyée au centre de formation.',
        kind: 'success',
      });
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de rejeter la formation',
        kind: 'error',
      });
    }
  };

  const handleFeatureTraining = async (trainingId: string, isFeatured: boolean) => {
    try {
      await adminService.featureTraining(trainingId, isFeatured);

      setTrainings(trainings.map(training =>
        training.id === trainingId ? {
          ...training,
          is_featured: isFeatured
        } : training
      ));

      // Update training details if it's the same training
      if (trainingDetails && trainingDetails.id === trainingId) {
        setTrainingDetails({ ...trainingDetails, is_featured: isFeatured });
      }

      setNotification({
        title: 'Succès',
        subtitle: isFeatured ? 'Formation mise en avant avec succès' : 'Formation retirée des formations en avant',
        kind: 'success',
      });
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de modifier le statut de mise en avant',
        kind: 'error',
      });
    }
  };

  const openTrainingDetails = async (training: Training) => {
    setSelectedTraining(training);
    setLoadingDetails(true);
    setIsModalOpen(true);
    try {
      const full = await adminService.getTrainingById(training.id);
      setTrainingDetails(full);
    } catch (e) {
      setTrainingDetails(training);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getLevelColor = (level: string) => {
    if (!level) return 'bg-gray-100 text-gray-800';
    switch (level.toLowerCase()) {
      case 'débutant':
        return 'bg-green-100 text-green-800';
      case 'intermédiaire':
        return 'bg-blue-100 text-blue-800';
      case 'avancé':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredTrainings = trainings.filter(training => {
    const matchesSearch = training.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         training.trainingCenter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (training.domaine.name_french || training.domaine.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && training.isActive) ||
                         (filterStatus === 'inactive' && !training.isActive);
    
    let matchesApproval = true;
    const trainingStatus = training.status || 'pending';
    
    if (filterApproval === 'approved') {
      matchesApproval = trainingStatus === 'approved';
    } else if (filterApproval === 'pending') {
      matchesApproval = trainingStatus === 'pending';
    } else if (filterApproval === 'rejected') {
      matchesApproval = trainingStatus === 'refused';
    }
    
    return matchesSearch && matchesStatus && matchesApproval;
  });

  const stats = {
    total: trainings.length,
    approved: trainings.filter(t => (t.status || 'pending') === 'approved').length,
    pending: trainings.filter(t => (t.status || 'pending') === 'pending').length,
    refused: trainings.filter(t => (t.status || 'pending') === 'refused').length,
    active: trainings.filter(t => t.isActive).length,
    totalViews: trainings.reduce((sum, training) => sum + (training.views || 0), 0),
    totalRevenue: trainings.reduce((sum, training) => sum + (training.pricing_dzd || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Formations</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Gérez toutes les formations de la plateforme PHINLEX</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          {/* Refresh Button */}
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => {
                loadTrainings();
                setLastRefresh(new Date());
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={14} className="text-gray-500" />
            </button>
          </div>
          
          {/* <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm sm:text-base">
            <Download size={16} />
            <span className="hidden sm:inline">Exporter</span>
            <span className="sm:hidden">Export</span>
          </button>
          <button className="bg-brand-primary hover:opacity-90 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm sm:text-base">
            <Plus size={16} />
            <span className="hidden sm:inline">Ajouter une formation</span>
            <span className="sm:hidden">Ajouter</span>
          </button> */}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-brand-primary/10 rounded-lg self-start">
              <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">Total formations</p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-green-100 rounded-lg self-start">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">Approuvées</p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{stats.approved}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-brand-accent/20 rounded-lg self-start">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-brand-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">En attente</p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{stats.pending}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-red-100 rounded-lg self-start">
              <XCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">Rejetées</p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{stats.refused}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-brand-secondary/20 rounded-lg self-start">
              <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-brand-secondary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">Actives</p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{stats.active}</h3>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-purple-100 rounded-lg self-start">
              <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">Vues totales</p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalViews.toLocaleString()}</h3>
            </div>
          </div>
        </div>
        
        {/* <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-emerald-100 rounded-lg self-start">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600">Revenus totaux</p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</h3>
            </div>
          </div>
        </div> */}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Rechercher des formations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm sm:text-base"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm sm:text-base"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
            <select
              value={filterApproval}
              onChange={(e) => {
                const value = e.target.value;
                setFilterApproval(value);
                if (value === 'all') {
                  setSearchParams({});
                } else {
                  setSearchParams({ filter: value });
                }
              }}
              className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm sm:text-base"
            >
              <option value="all">Toutes les approbations</option>
              <option value="approved">Approuvées</option>
              <option value="pending">En attente</option>
              <option value="rejected">Rejetées</option>
            </select>
            <button
              onClick={() => { 
                setSearchTerm(''); 
                setFilterStatus('all'); 
                setFilterApproval('all'); 
                setSearchParams({});
              }}
              className="px-3 sm:px-4 py-2 border border-brand-primary/30 text-brand-primary hover:bg-brand-primary/5 rounded-lg transition-colors text-sm sm:text-base"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Trainings Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Formation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Centre de formation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Domaine
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Niveau
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vues
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscriptions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approbation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTrainings.map((training) => (
                <tr 
                  key={training.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => openTrainingDetails(training)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                        <GraduationCap className="h-5 w-5 text-brand-primary" />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{training.title}</span>
                          {training.is_featured && (
                            <div className="flex items-center space-x-1">
                              <Star className="h-4 w-4 text-yellow-500 fill-current" />
                              <span className="text-xs text-yellow-600 font-medium">En avant</span>
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{training.duration}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{training.trainingCenter.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <BookOpen className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{training.domaine.name_french || training.domaine.name || 'N/A'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor(training.level || '')}`}>
                      {training.level || 'Non spécifié'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(training.pricing_dzd)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Eye className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{training.views}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{training.subscriptionsCount || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {training.isActive ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 mr-2" />
                      )}
                      <span className={`text-sm ${training.isActive ? 'text-green-600' : 'text-red-600'}`}>
                        {training.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ApprovalStatusBadge 
                      status={training.status || 'pending'}
                      size="sm"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        className="text-brand-primary hover:opacity-90"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTrainingDetails(training);
                        }}
                      >
                        <Eye size={16} />
                      </button>
                    
                      <button 
                        className="text-red-600 hover:text-red-900"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTraining(training);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredTrainings.length === 0 && (
          <div className="text-center py-12">
            <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucune formation trouvée</p>
          </div>
        )}
      </div>

      {/* Training Details Modal */}
      {isModalOpen && selectedTraining && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Détails de la formation</h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setSelectedTraining(null);
                    setTrainingDetails(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <GraduationCap size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">
                    {trainingDetails?.title || selectedTraining.title}
                  </h3>
                  <p className="text-gray-600">{trainingDetails?.trainingCenter?.name || selectedTraining.trainingCenter.name}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">Informations générales</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm">Domaine</p>
                      <span className="text-sm font-medium">{trainingDetails?.domaine?.name_french || trainingDetails?.domaine?.name || selectedTraining.domaine.name_french || selectedTraining.domaine.name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm">Niveau</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor((trainingDetails?.level || selectedTraining.level) || '')}`}>
                        {trainingDetails?.level || selectedTraining.level || 'Non spécifié'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm">Durée</p>
                      <p className="text-sm font-medium">{trainingDetails?.duration || selectedTraining.duration}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm">Prix</p>
                      <p className="text-sm font-medium">{formatCurrency(trainingDetails?.pricing_dzd ?? selectedTraining.pricing_dzd)}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">Statistiques</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm">Vues</p>
                      <div className="flex items-center space-x-2">
                        <Eye size={16} className="text-gray-500" />
                        <p className="text-sm font-medium">{trainingDetails?.views ?? selectedTraining.views}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm">Statut</p>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        (trainingDetails?.isActive ?? selectedTraining.isActive) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(trainingDetails?.isActive ?? selectedTraining.isActive) ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm">Approbation</p>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        (trainingDetails?.isApproved ?? selectedTraining.isApproved) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {(trainingDetails?.isApproved ?? selectedTraining.isApproved) ? 'Approuvé' : 'En attente'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-sm">Mise en avant</p>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        (trainingDetails?.is_featured ?? selectedTraining.is_featured) 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        <Star className={`h-3 w-3 mr-1 ${(trainingDetails?.is_featured ?? selectedTraining.is_featured) ? 'text-yellow-600 fill-current' : 'text-gray-500'}`} />
                        {(trainingDetails?.is_featured ?? selectedTraining.is_featured) ? 'En avant' : 'Standard'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Description complète</h4>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{trainingDetails?.full_description || trainingDetails?.description || selectedTraining.description || 'Aucune description disponible'}</p>
              </div>

              {(trainingDetails?.learning_objectives || trainingDetails?.skills_gained || trainingDetails?.target_audience) && (
                <div className="border-t pt-4 space-y-4">
                  {trainingDetails?.learning_objectives && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Objectifs d'apprentissage</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{trainingDetails.learning_objectives}</p>
                    </div>
                  )}
                  {trainingDetails?.skills_gained && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Compétences acquises</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{trainingDetails.skills_gained}</p>
                    </div>
                  )}
                  {trainingDetails?.target_audience && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Public cible</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{trainingDetails.target_audience}</p>
                    </div>
                  )}
                </div>
              )}

              {(trainingDetails?.trainer || trainingDetails?.language || trainingDetails?.schedule_date) && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Informations supplémentaires</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {trainingDetails?.trainer && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Formateur</p>
                        <p className="text-sm text-gray-600">{trainingDetails.trainer}</p>
                      </div>
                    )}
                    {trainingDetails?.language && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Langue</p>
                        <p className="text-sm text-gray-600">{trainingDetails.language}</p>
                      </div>
                    )}
                    {trainingDetails?.schedule_date && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Date programmée</p>
                        <p className="text-sm text-gray-600">{formatDate(trainingDetails.schedule_date)}</p>
                      </div>
                    )}
                    {trainingDetails?.format && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Format</p>
                        <p className="text-sm text-gray-600">{trainingDetails.format}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(trainingDetails?.startDate || selectedTraining.startDate) && (trainingDetails?.endDate || selectedTraining.endDate) && (
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Date de début</p>
                      <p className="text-sm">{formatDate((trainingDetails?.startDate || selectedTraining.startDate) as string)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Date de fin</p>
                      <p className="text-sm">{formatDate((trainingDetails?.endDate || selectedTraining.endDate) as string)}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar size={16} className="text-gray-500" />
                  <p className="text-sm font-medium text-gray-700">Date de création</p>
                </div>
                <p className="text-sm">{formatDate(trainingDetails?.createdAt || selectedTraining.createdAt)}</p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                {loadingDetails ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary"></div>
                    <span className="text-sm text-gray-500">Chargement des détails...</span>
                  </div>
                ) : (
                  <>
                    {!selectedTraining.isApproved && (
                      <>
                        <button
                          className="px-4 py-2 rounded-lg bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition-colors"
                          onClick={() => setIsRejectModalOpen(true)}
                          disabled={!trainingDetails}
                        >
                          Rejeter
                        </button>
                        <button
                          className="px-4 py-2 rounded-lg bg-brand-primary text-white hover:opacity-90 transition-colors"
                          onClick={async () => { 
                            await handleApproveTraining(selectedTraining.id); 
                            setIsModalOpen(false);
                            setSelectedTraining(null);
                            setTrainingDetails(null);
                          }}
                          disabled={!trainingDetails}
                        >
                          Approuver
                        </button>
                      </>
                    )}
                    {selectedTraining.isApproved && (
                      <>
                        <button
                          className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                            (trainingDetails?.is_featured ?? selectedTraining.is_featured) 
                              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                              : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                          }`}
                          onClick={() => handleFeatureTraining(
                            selectedTraining.id, 
                            !(trainingDetails?.is_featured ?? selectedTraining.is_featured)
                          )}
                          disabled={!trainingDetails}
                        >
                          <Sparkles size={16} />
                          <span>
                            {(trainingDetails?.is_featured ?? selectedTraining.is_featured) 
                              ? 'Retirer des formations en avant' 
                              : 'Mettre en avant'
                            }
                          </span>
                        </button>
                        <button 
                          className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors" 
                          onClick={() => {
                            setIsModalOpen(false);
                            setSelectedTraining(null);
                            setTrainingDetails(null);
                          }}
                        >
                          Fermer
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Training Modal */}
      {isRejectModalOpen && selectedTraining && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <AlertCircle size={24} className="text-red-500" />
                <h3 className="text-lg font-medium">Rejeter la formation</h3>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-gray-600">
                Veuillez fournir une raison pour le rejet de la formation "{selectedTraining.title}".
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Entrez la raison du rejet..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setIsRejectModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleRejectTraining}
                disabled={!rejectReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Rejeter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
          notification.kind === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
          notification.kind === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
          notification.kind === 'warning' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
          'bg-blue-100 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center space-x-2">
            {notification.kind === 'success' && <CheckCircle size={16} />}
            {notification.kind === 'error' && <XCircle size={16} />}
            {notification.kind === 'warning' && <AlertCircle size={16} />}
            <div>
              <p className="font-medium">{notification.title}</p>
              <p className="text-sm">{notification.subtitle}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-gray-500 hover:text-gray-700"
            >
              <XCircle size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingsPage;
